import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import { EventLogTypeGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { PermissionGuard } from '~/core/PermissionGuard';
import { useCreateThreadWithCommentMutation } from '~/packages/comments/mutations/CreateThreadWithComment.gql.generated';
import { EventLogDrawer } from '~/packages/event-logs/EventLogDrawer';

// Constants
const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [
  'valleyBank_loanAccount_thread_create',
];
const SINGLE_ENTITY_DRAWER_LABEL = 'Loan Account Activity';
const MULTI_ENTITY_DRAWER_LABEL = 'Loan Account Activity';

// Exports
type SingleEntityDrawerProps = Partial<
  Omit<React.ComponentProps<typeof EventLogDrawer>, 'entityIds'>
> & {
  entityId: string;
};
export const SingleEntityDrawer = (props: SingleEntityDrawerProps) => {
  const toast = useToast();
  const [createThread] = useCreateThreadWithCommentMutation();

  return (
    <PermissionGuard
      permissions={(ctx) =>
        ctx.flags.productsValleyBankLoanAccountsEventLogPermissions
      }
    >
      <EventLogDrawer
        {...props}
        entityIds={[props.entityId]}
        header={SINGLE_ENTITY_DRAWER_LABEL}
        triggerButtonProps={{
          variant: 'v2-ghost',
          'aria-label': SINGLE_ENTITY_DRAWER_LABEL,
          placement: 'bottom',
        }}
        entityThreadDomain="valleyBank_loanAccountNumber"
        eventLogTypes={EVENT_LOG_TYPES}
        onCreateThread={async ({ comment, participatingOrganizationIds }) => {
          try {
            const result = await createThread({
              variables: {
                comment,
                domain: 'valleyBank_loanAccountNumber',
                domainIdentifier: props.entityId,
                // Valley SBL threads are pinned by default
                isPinned: true,
                participatingOrganizationIds,
              },
              refetchQueries: ['GetEventLogs'],
            });
            if (!result.data) throw new Error('Failed to create thread');
            const commentId = result.data?.thread.comments[0].id;
            if (!commentId) throw new Error('Failed to create comment');
          } catch (error) {
            Sentry.captureException(error);
            toast({
              title: 'Failed to create thread',
              status: 'error',
              position: 'top',
            });
          }
        }}
      />
    </PermissionGuard>
  );
};

type MultiEntityDrawerProps = Partial<
  Omit<React.ComponentProps<typeof EventLogDrawer>, 'entityIds'>
>;
export const MultiEntityDrawer = (props: MultiEntityDrawerProps) => {
  return (
    <PermissionGuard
      permissions={(ctx) =>
        ctx.flags.productsValleyBankLoanAccountsEventLogPermissions
      }
    >
      <EventLogDrawer
        {...props}
        entityIds={undefined}
        eventLogTypes={EVENT_LOG_TYPES}
        entityThreadDomain="valleyBank_loanAccountNumber"
        header={MULTI_ENTITY_DRAWER_LABEL}
        triggerButtonProps={{ 'aria-label': MULTI_ENTITY_DRAWER_LABEL }}
      />
    </PermissionGuard>
  );
};
